import { power } from "@/lib/tool-kit/arithmetic/power";
import {
  absBig,
  BIG_ONE,
  BIG_ZERO,
  formatBig,
} from "@/lib/tool-kit/math/bigint";
import {
  addR,
  divR,
  formatRationalNice,
  makeRational,
  mulR,
  negR,
  type Rational,
  subR,
} from "@/lib/tool-kit/math/rational";

/**
 * Evaluate an arithmetic expression one reduction at a time, in the order school rules
 * require, with every intermediate value an exact fraction.
 *
 * The rules, tightest first: brackets (innermost first); exponents, right to left so that
 * 2^3^2 = 2^9; a minus sign in front of a power or bracket (so −2² = −4); then
 * multiplication and division together, left to right; then addition and subtraction
 * together, left to right. "PEMDAS" and "BODMAS" are two names for the same ranking.
 *
 * Implicit multiplication — 2(3 + 4), (1 + 2)(3 + 4) — is treated as ordinary
 * multiplication by default. Textbooks and calculators disagree about 8 ÷ 2(2 + 2), so
 * a second reading, where juxtaposition binds tighter than ÷, is available.
 */
export type ImplicitMode = "same" | "tighter";

export type Stage =
  | "brackets"
  | "exponents"
  | "negate"
  | "implicit"
  | "muldiv"
  | "addsub";

export interface EvaluationStep {
  stage: Stage;
  /** The reduction in symbols: "3 × 4 = 12". */
  operation: string;
  /** The whole expression after this step. */
  after: string;
  /** The step happened inside brackets. */
  inBrackets: boolean;
  /** Brackets that became a single number were removed in this step. */
  bracketsDropped: boolean;
}

export type EvaluationError =
  | "empty"
  | "too-long"
  | "bad-character"
  | "comma"
  | "missing-operand"
  | "missing-operator"
  | "unbalanced"
  | "mismatched"
  | "divide-by-zero"
  | "zero-negative-power"
  | "not-rational"
  | "too-large"
  | "too-many-steps";

export interface EvaluationFlags {
  /** A bare minus sign sits in front of a power, as in −2². */
  minusBeforePower: boolean;
  /** Chained exponents, as in 2^3^2. */
  chainedPowers: boolean;
  /** Something like 8 ÷ 2(2 + 2), where two readings give different answers. */
  ambiguousImplicit: boolean;
}

export type Evaluation =
  | {
      ok: true;
      read: string;
      steps: EvaluationStep[];
      result: Rational;
      flags: EvaluationFlags;
    }
  | { ok: false; error: EvaluationError; token: string };

export const MAX_EXPRESSION = 200;
const MAX_STEPS = 120;
const MAX_VALUE_DIGITS = 1500;

/* ------------------------------------------------------------- tokens */

type Op = "+" | "-" | "*" | "/" | "^" | "imul";

type Tok =
  | { t: "num"; v: Rational; text: string | null }
  | { t: "op"; op: Op }
  | { t: "neg" }
  | { t: "open"; kind: string }
  | { t: "close"; kind: string };

const NUMBER = /\d+\.?\d*|\.\d+/y;
const OPENERS: Readonly<Record<string, string>> = {
  "(": ")",
  "[": "]",
  "{": "}",
};
const CLOSERS: Readonly<Record<string, string>> = {
  ")": "(",
  "]": "[",
  "}": "{",
};
const OPERATORS: Readonly<Record<string, Op>> = {
  "+": "+",
  "-": "-",
  "−": "-",
  "–": "-",
  "*": "*",
  "×": "*",
  "·": "*",
  "⋅": "*",
  "/": "/",
  "÷": "/",
  ":": "/",
  "^": "^",
};
const DOUBLE_STAR = /\*\*/g;
const SPACES = /\s+/g;
const SPLIT_NUMBER = /[\d.]\s+[\d.]/;

function decimalToRational(text: string): Rational {
  const point = text.indexOf(".");
  const digits = text.replace(".", "") || "0";
  if (point === -1) {
    return makeRational(BigInt(digits));
  }
  const places = text.length - point - 1;
  return makeRational(BigInt(digits), BigInt(10) ** BigInt(places));
}

type Raw =
  | { t: "num"; v: Rational; text: string }
  | { t: "op"; op: Exclude<Op, "imul"> }
  | { t: "open"; kind: string }
  | { t: "close"; kind: string };

function symbolToken(ch: string): Raw | null {
  if (OPENERS[ch]) {
    return { t: "open", kind: ch };
  }
  if (CLOSERS[ch]) {
    return { t: "close", kind: ch };
  }
  if (OPERATORS[ch]) {
    return { t: "op", op: OPERATORS[ch] as Exclude<Op, "imul"> };
  }
  return null;
}

function scan(
  source: string
): Raw[] | { error: EvaluationError; token: string } {
  if (SPLIT_NUMBER.test(source)) {
    return { error: "missing-operator", token: "" };
  }
  const text = source.replace(DOUBLE_STAR, "^").replace(SPACES, "");
  const out: Raw[] = [];
  let at = 0;
  while (at < text.length) {
    const ch = text[at];
    NUMBER.lastIndex = at;
    const number = NUMBER.exec(text);
    if (number && number.index === at) {
      out.push({ t: "num", v: decimalToRational(number[0]), text: number[0] });
      at += number[0].length;
      continue;
    }
    if (ch === ",") {
      return { error: "comma", token: ch };
    }
    const symbol = symbolToken(ch);
    if (symbol === null) {
      return { error: "bad-character", token: ch };
    }
    out.push(symbol);
    at += 1;
  }
  return out;
}

/**
 * A leading minus becomes part of a following number — unless a power follows, because
 * −2^2 means −(2^2) — or else a separate negation. A leading plus vanishes.
 */
function unarySign(
  raw: readonly Raw[],
  i: number
): { tokens: Tok[]; skip: number } {
  const token = raw[i];
  if (token.t !== "op" || token.op === "+") {
    return { tokens: [], skip: 0 };
  }
  const next = raw[i + 1];
  const after = raw[i + 2];
  const feedsPower = after?.t === "op" && after.op === "^";
  if (next?.t === "num" && !feedsPower) {
    return {
      tokens: [{ t: "num", v: negR(next.v), text: `−${next.text}` }],
      skip: 1,
    };
  }
  return { tokens: [{ t: "neg" }], skip: 0 };
}

function isSignOp(token: Raw): boolean {
  return token.t === "op" && (token.op === "+" || token.op === "-");
}

/** Resolve unary signs and insert the multiplication that juxtaposition implies. */
function structure(raw: Raw[]): Tok[] {
  const out: Tok[] = [];
  for (let i = 0; i < raw.length; i++) {
    const token = raw[i];
    const previous = raw[i - 1];
    const operandBefore =
      previous !== undefined &&
      (previous.t === "num" || previous.t === "close");
    if (isSignOp(token) && !operandBefore) {
      const sign = unarySign(raw, i);
      out.push(...sign.tokens);
      i += sign.skip;
      continue;
    }
    if (operandBefore && (token.t === "num" || token.t === "open")) {
      out.push({ t: "op", op: "imul" });
    }
    out.push(token);
  }
  return out;
}

interface ValidationState {
  expectOperand: boolean;
  stack: string[];
}

function validateToken(
  token: Tok,
  state: ValidationState
): EvaluationError | null {
  if (token.t === "num" || token.t === "open") {
    if (!state.expectOperand) {
      return "missing-operator";
    }
    state.expectOperand = token.t === "open";
    if (token.t === "open") {
      state.stack.push(token.kind);
    }
    return null;
  }
  if (token.t === "close") {
    const opened = state.stack.pop();
    if (state.expectOperand) {
      return "missing-operand";
    }
    if (opened === undefined) {
      return "unbalanced";
    }
    return OPENERS[opened] === token.kind ? null : "mismatched";
  }
  if (token.t === "neg") {
    return state.expectOperand ? null : "missing-operator";
  }
  if (state.expectOperand) {
    return "missing-operand";
  }
  state.expectOperand = true;
  return null;
}

function validate(tokens: readonly Tok[]): EvaluationError | null {
  const state: ValidationState = { expectOperand: true, stack: [] };
  for (const token of tokens) {
    const problem = validateToken(token, state);
    if (problem) {
      return problem;
    }
  }
  if (state.expectOperand) {
    return "missing-operand";
  }
  return state.stack.length > 0 ? "unbalanced" : null;
}

/* ------------------------------------------------------------- display */

function showValue(v: Rational): string {
  const text = formatRationalNice(v, ".", 12);
  return text.includes("/") ? `(${text})` : text;
}

function numText(token: Extract<Tok, { t: "num" }>): string {
  return token.text ?? showValue(token.v);
}

const SYMBOL: Record<Op, string> = {
  "+": " + ",
  "-": " − ",
  "*": " × ",
  "/": " ÷ ",
  "^": "^",
  imul: " × ",
};

function render(tokens: readonly Tok[]): string {
  let out = "";
  for (const [index, token] of tokens.entries()) {
    const previous = tokens[index - 1];
    if (token.t === "num") {
      const text = numText(token);
      const afterOp = previous?.t === "op" || previous?.t === "neg";
      out += text.startsWith("−") && afterOp ? `(${text})` : text;
    } else if (token.t === "op") {
      out += SYMBOL[token.op];
    } else if (token.t === "neg") {
      out += "−";
    } else if (token.t === "open") {
      out += token.kind;
    } else {
      out += token.kind;
    }
  }
  return out;
}

function valueDigits(v: Rational): number {
  return absBig(v.num).toString().length + v.den.toString().length;
}

/* ------------------------------------------------------------ reduction */

interface Step {
  tokens: Tok[];
  stage: Stage;
  operation: string;
}

interface Failure {
  error: EvaluationError;
  token: string;
}

function isNum(token: Tok | undefined): token is Extract<Tok, { t: "num" }> {
  return token?.t === "num";
}

function applyBinary(
  op: Op,
  left: Rational,
  right: Rational
): Rational | Failure {
  switch (op) {
    case "+":
      return addR(left, right);
    case "-":
      return subR(left, right);
    case "*":
    case "imul":
      return mulR(left, right);
    case "/":
      return right.num === BIG_ZERO
        ? { error: "divide-by-zero", token: "÷" }
        : divR(left, right);
    default: {
      const result = power(left, right);
      if (result.kind === "exact") {
        return result.value;
      }
      if (result.kind === "undefined") {
        return { error: "zero-negative-power", token: "^" };
      }
      return {
        error: result.kind === "huge" ? "too-large" : "not-rational",
        token: "^",
      };
    }
  }
}

function isFailure(value: Rational | Failure): value is Failure {
  return "error" in value;
}

/** Reduce the binary operator at `index`, whose operands are the numbers either side. */
function reduceAt(
  tokens: readonly Tok[],
  index: number,
  stage: Stage
): Step | Failure {
  const left = tokens[index - 1];
  const right = tokens[index + 1];
  const op = tokens[index];
  if (!(isNum(left) && isNum(right) && op.t === "op")) {
    return { error: "missing-operand", token: "" };
  }
  const value = applyBinary(op.op, left.v, right.v);
  if (isFailure(value)) {
    return value;
  }
  if (valueDigits(value) > MAX_VALUE_DIGITS) {
    return { error: "too-large", token: "" };
  }
  const symbol = SYMBOL[op.op].trim();
  const wrap = (token: Extract<Tok, { t: "num" }>) => {
    const text = numText(token);
    return text.startsWith("−") ? `(${text})` : text;
  };
  const joiner = op.op === "^" ? symbol : ` ${symbol} `;
  const operation = `${wrap(left)}${joiner}${wrap(right)} = ${showValue(value)}`;
  const next: Tok[] = [
    ...tokens.slice(0, index - 1),
    { t: "num", v: value, text: null },
    ...tokens.slice(index + 2),
  ];
  return { tokens: next, stage, operation };
}

function negateAt(tokens: readonly Tok[], index: number): Step | null {
  const operand = tokens[index + 1];
  if (!isNum(operand)) {
    return null;
  }
  const value = negR(operand.v);
  return {
    tokens: [
      ...tokens.slice(0, index),
      { t: "num", v: value, text: null },
      ...tokens.slice(index + 2),
    ],
    stage: "negate",
    operation: `−(${numText(operand)}) = ${showValue(value)}`,
  };
}

function lastIndexWhere(
  tokens: readonly Tok[],
  from: number,
  to: number,
  predicate: (token: Tok, index: number) => boolean
): number {
  for (let i = to - 1; i >= from; i--) {
    if (predicate(tokens[i], i)) {
      return i;
    }
  }
  return -1;
}

function firstIndexWhere(
  tokens: readonly Tok[],
  from: number,
  to: number,
  predicate: (token: Tok, index: number) => boolean
): number {
  for (let i = from; i < to; i++) {
    if (predicate(tokens[i], i)) {
      return i;
    }
  }
  return -1;
}

/** One reduction inside tokens[from, to). */
function nextReduction(
  tokens: readonly Tok[],
  from: number,
  to: number,
  implicit: ImplicitMode
): Step | Failure | null {
  const isPower = (t: Tok) => t.t === "op" && t.op === "^";
  const powerIndex = lastIndexWhere(tokens, from, to, isPower);
  if (powerIndex !== -1) {
    const right = tokens[powerIndex + 1];
    if (right?.t === "neg" && isNum(tokens[powerIndex + 2])) {
      return negateAt(tokens, powerIndex + 1);
    }
    if (isNum(tokens[powerIndex - 1]) && isNum(right)) {
      return reduceAt(tokens, powerIndex, "exponents");
    }
  }
  const neg = lastIndexWhere(
    tokens,
    from,
    to,
    (t, i) => t.t === "neg" && isNum(tokens[i + 1])
  );
  if (neg !== -1) {
    return negateAt(tokens, neg);
  }
  const isOp = (ops: readonly Op[]) => (t: Tok, i: number) =>
    t.t === "op" &&
    ops.includes(t.op) &&
    isNum(tokens[i - 1]) &&
    isNum(tokens[i + 1]);
  if (implicit === "tighter") {
    const imul = firstIndexWhere(tokens, from, to, isOp(["imul"]));
    if (imul !== -1) {
      return reduceAt(tokens, imul, "implicit");
    }
  }
  const mul = firstIndexWhere(tokens, from, to, isOp(["*", "/", "imul"]));
  if (mul !== -1) {
    return reduceAt(tokens, mul, "muldiv");
  }
  const add = firstIndexWhere(tokens, from, to, isOp(["+", "-"]));
  if (add !== -1) {
    return reduceAt(tokens, add, "addsub");
  }
  return null;
}

function innermost(
  tokens: readonly Tok[]
): { open: number; close: number } | null {
  const close = tokens.findIndex((t) => t.t === "close");
  if (close === -1) {
    return null;
  }
  const open = lastIndexWhere(tokens, 0, close, (t) => t.t === "open");
  return { open, close };
}

/** Drop brackets that now hold a single number. Returns the new tokens or null. */
function dropBrackets(tokens: readonly Tok[]): Tok[] | null {
  for (let i = 0; i + 2 < tokens.length; i++) {
    if (
      tokens[i].t === "open" &&
      isNum(tokens[i + 1]) &&
      tokens[i + 2].t === "close"
    ) {
      return [...tokens.slice(0, i), tokens[i + 1], ...tokens.slice(i + 3)];
    }
  }
  return null;
}

/* --------------------------------------------------------------- flags */

function groupEnd(tokens: readonly Tok[], start: number): number {
  // Index just past the operand beginning at `start` (a number or a bracket group).
  if (tokens[start]?.t !== "open") {
    return start + 1;
  }
  let depth = 0;
  for (let j = start; j < tokens.length; j++) {
    if (tokens[j].t === "open") {
      depth += 1;
    } else if (tokens[j].t === "close") {
      depth -= 1;
    }
    if (depth === 0) {
      return j + 1;
    }
  }
  return tokens.length;
}

function isPowerOp(token: Tok | undefined): boolean {
  return token?.t === "op" && token.op === "^";
}

function flagsFor(tokens: readonly Tok[]): EvaluationFlags {
  const flags: EvaluationFlags = {
    minusBeforePower: false,
    chainedPowers: false,
    ambiguousImplicit: false,
  };
  for (const [i, token] of tokens.entries()) {
    if (token.t === "neg" && isPowerOp(tokens[groupEnd(tokens, i + 1)])) {
      flags.minusBeforePower = true;
    }
    if (isPowerOp(token)) {
      const start = tokens[i + 1]?.t === "neg" ? i + 2 : i + 1;
      if (isPowerOp(tokens[groupEnd(tokens, start)])) {
        flags.chainedPowers = true;
      }
    }
    if (token.t === "op" && token.op === "imul") {
      const divides =
        tokens[i - 2]?.t === "op" && (tokens[i - 2] as { op: Op }).op === "/";
      if (isNum(tokens[i - 1]) && divides) {
        flags.ambiguousImplicit = true;
      }
    }
  }
  return flags;
}

/* ------------------------------------------------------------- driver */

/** One loop iteration: drop lone brackets, or do the next reduction. */
function advance(
  tokens: Tok[],
  implicit: ImplicitMode
): { tokens: Tok[]; step: EvaluationStep } | Failure {
  const dropped = dropBrackets(tokens);
  if (dropped) {
    return {
      tokens: dropped,
      step: {
        stage: "brackets",
        operation: "",
        after: render(dropped),
        inBrackets: true,
        bracketsDropped: true,
      },
    };
  }
  const group = innermost(tokens);
  const from = group ? group.open + 1 : 0;
  const to = group ? group.close : tokens.length;
  const reduction = nextReduction(tokens, from, to, implicit);
  if (reduction === null) {
    return { error: "missing-operand", token: "" };
  }
  if ("error" in reduction) {
    return reduction;
  }
  const closing = dropBrackets(reduction.tokens);
  const lone = group !== null && closing !== null;
  const next = lone && closing ? closing : reduction.tokens;
  return {
    tokens: next,
    step: {
      stage: reduction.stage,
      operation: reduction.operation,
      after: render(next),
      inBrackets: group !== null,
      bracketsDropped: lone,
    },
  };
}

export function evaluateSteps(
  source: string,
  implicit: ImplicitMode
): Evaluation {
  const trimmed = source.trim();
  if (trimmed === "") {
    return { ok: false, error: "empty", token: "" };
  }
  if (trimmed.length > MAX_EXPRESSION) {
    return { ok: false, error: "too-long", token: "" };
  }
  const raw = scan(trimmed);
  if (!Array.isArray(raw)) {
    return { ok: false, ...raw };
  }
  const original = structure(raw);
  const invalid = validate(original);
  if (invalid) {
    return { ok: false, error: invalid, token: "" };
  }
  let tokens: Tok[] = original;
  const steps: EvaluationStep[] = [];
  while (tokens.length > 1) {
    if (steps.length >= MAX_STEPS) {
      return { ok: false, error: "too-many-steps", token: "" };
    }
    const result = advance(tokens, implicit);
    if ("error" in result) {
      return { ok: false, ...result };
    }
    tokens = result.tokens;
    steps.push(result.step);
  }
  const last = tokens[0];
  if (!isNum(last)) {
    return { ok: false, error: "missing-operand", token: "" };
  }
  return {
    ok: true,
    read: render(original),
    steps,
    result: last.v,
    flags: flagsFor(original),
  };
}

/** Fraction, decimal and mixed forms of the answer, for display. */
export function resultForms(value: Rational): {
  integer: boolean;
  fraction: string;
  mixed: string | null;
} {
  if (value.den === BIG_ONE) {
    return { integer: true, fraction: formatBig(value.num), mixed: null };
  }
  const magnitude = absBig(value.num);
  const whole = magnitude / value.den;
  const rest = magnitude % value.den;
  const sign = value.num < BIG_ZERO ? "−" : "";
  return {
    integer: false,
    fraction: `${formatBig(value.num)}/${value.den}`,
    mixed: whole === BIG_ZERO ? null : `${sign}${whole} ${rest}/${value.den}`,
  };
}
